'use client';
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter, usePathname } from 'src/navigation';
import { cleanURLParams } from 'helpers/search/cleanURLParams';
import type { IPressSearchResult } from 'src/app/[locale]/(navigation)/press-releases/data';
import { SITE_SEARCH_PER_PAGE } from 'lib/constants.mjs';
import styles from './press-search.module.scss';
import SearchLayout from 'components/search/search-layout';
import FlexContainer from 'components/containers/flex-container';
import ClearableFilters from 'components/controls/filters/clearable-filters';
import SimpleSelect from 'components/controls/simple-select';
import PressCard from 'components/cards/press-card';
import PageSection from 'components/containers/page-section';
import PaginationControls from 'components/pagination';
import CollapsibleDrawer from 'components/collapsible-drawer';
import CheckboxGroupHeading from 'components/controls/checkbox-group-heading';
import CheckboxWithLabel from 'components/controls/checkbox-with-label';
import { resultsString } from 'helpers/search/resultsString';

const PER_PAGE = SITE_SEARCH_PER_PAGE;

const sortByFields = {
	releaseDate: {
		name: 'Release Date (newest first)',
		filter: [{ releaseDate: { order: 'desc', missing: '_last' } }],
	},
	releaseDateAsc: {
		name: 'Release Date (oldest first)',
		filter: [{ releaseDate: { order: 'asc', missing: '_last' } }],
	},
	relevance: { name: 'Relevance', filter: undefined }, // default behavior, no filter needed
};

const ARRAY_PARAMS = ['departments', 'topics'];
const deserializeParams = (params) => {
	Object.keys(params).forEach((key) => {
		if (ARRAY_PARAMS.includes(key)) {
			params[key] = `${params[key]}`.split(',');
		}
	});
	return params;
};

const paramsSanityCheck = (params) => {
	if (!!params.q && params.sortBy === 'relevance') {
		params.sortBy = undefined;
	}
	return cleanURLParams(params);
};

const PressSearch = ({
	results,
	total,
	aggs,
	searchParams,
	pressTopics,
	departments,
	page,
}: IPressSearchResult) => {
	const pathname = usePathname();
	const router = useRouter();
	const paramsHook = useSearchParams();
	const [params, setParams] = useState(deserializeParams(searchParams));

	const allDepartments = useMemo(() => {
		return departments.reduce(
			(results, department) => {
				if (['curatorial', 'conservation'].includes(department?.type)) {
					results[department.type].push(department);
				} else {
					results.other.push(department);
				}
				return results;
			},
			{
				curatorial: [],
				conservation: [],
				other: [],
			},
		);
	}, [departments]);

	const isChecked = (type, value) =>
		!!params[type]?.find((param) => param == value);

	const getCountBySlug = (type, value) => {
		return (
			aggs.aggregations[type]?.buckets?.find((agg) => agg.key == value)
				?.doc_count || 0
		);
	};

	const findFacets = (facetName, facets) => {
		if (!['departments', 'topics'].includes(facetName)) {
			return;
		}

		const lookup = facetName == 'departments' ? departments : pressTopics;
		return {
			name: facetName,
			selectedValues: lookup
				.filter((facet) => facets.includes(facet.slug))
				.map((facet) => ({
					label: facet.title,
					value: facet.slug,
					facet: facetName,
				})),
		};
	};

	const mapParamsToClearableFilters = () => {
		const result = Object.keys(params)
			.map((facetName) => {
				if (Array.isArray(params[facetName])) {
					return findFacets(facetName, params[facetName]);
				}
			})
			.filter(Boolean);

		return result;
	};

	// search query is set outside the scope of this app, so make sure this app is in sync
	const setParamsWithQuery = (newParams) => {
		const q = paramsHook.get('q');
		setParams(cleanURLParams({ ...newParams, q: q ? q : undefined }));
	};

	const handleClearOne = (e, facetName) => {
		const { value } = e.target;
		const newParams = { ...params, page: 1 };

		const currentValues = newParams?.[facetName] || [];
		const index = currentValues.indexOf(value);
		currentValues.splice(index, 1);
		newParams[facetName] = currentValues;

		setParamsWithQuery(newParams);
	};

	const handleClearAll = () => {
		const newParams = { ...params, topics: [], departments: [] };
		setParamsWithQuery(newParams);
	};

	const handlePageChange = (e) => {
		window.scrollTo(0, 0);
		const newPage = e.currentTarget.value / PER_PAGE + page;
		const newParams = { ...params, page: newPage };
		setParamsWithQuery(newParams);
	};

	const handleSortChange = (event) => {
		const newParams = { ...params, page: 1, sortBy: event.target.value };
		setParamsWithQuery(newParams);
	};

	const handleCheckboxChange = (e) => {
		const { name, value, checked } = e.target;
		const newParams = { ...params, page: 1 };

		const currentValues = newParams?.[name] || [];
		if (checked) {
			currentValues?.push(value);
		} else {
			const index = currentValues.indexOf(value);
			currentValues.splice(index, 1);
		}

		newParams[name] = currentValues;

		setParamsWithQuery(newParams);
	};

	useEffect(() => {
		paramsSanityCheck(params);
		router.push(`${pathname}?${new URLSearchParams(params)}`, {
			scroll: false,
		});
	}, [params, pathname, router]);

	return (
		<PageSection>
			<SearchLayout inverted={true}>
				{/* Main view */}
				<div>
					{!!mapParamsToClearableFilters()?.length && (
						<FlexContainer
							gap="var(--spacing-micro)"
							wrap="wrap"
							className={styles.pills}
						>
							<ClearableFilters
								groupName="press-facets"
								handleClearAll={handleClearAll}
								handleClose={handleClearOne}
								facets={mapParamsToClearableFilters()}
							/>
						</FlexContainer>
					)}
					<div className={styles.resultsCountSection}>
						<div className={styles.resultCount}>
							{resultsString({ total, page, perPage: PER_PAGE })}
						</div>
						<div className={styles.sortControl}>
							<span className={styles.sortByTitle}>Sort by:</span>
							<SimpleSelect
								id={'sort-by'}
								initialValue={
									params?.sortBy
										? params.sortBy
										: params.q
											? 'relevance'
											: 'publicationDate'
								}
								options={Object.entries(sortByFields).map(
									([value, { name }]) => {
										return {
											name,
											value,
											// relevance is not an option without a text search, so disable it
											isDisabled: !paramsHook.get('q') && value === 'relevance',
										};
									},
								)}
								handleChange={handleSortChange}
							/>
						</div>
					</div>
					<div className={styles.pressReleasesContainer}>
						{!!results?.length &&
							results.map((pressRelease) => {
								const { sanityId } = pressRelease;
								return <PressCard key={sanityId} {...pressRelease} />;
							})}
					</div>
					<PageSection>
						<PaginationControls
							offset={(page - 1) * PER_PAGE}
							handlePaginationChange={handlePageChange}
							perPage={PER_PAGE}
							totalResults={total}
						/>
					</PageSection>
				</div>

				{/* Filters */}
				<div>
					<div className={styles.filterHeading}>
						<h4>Narrow your Search</h4>
					</div>

					{/* Topics */}
					<CollapsibleDrawer
						open={false}
						heading={
							<CheckboxGroupHeading
								name="Topic"
								count={params?.topics?.length}
							/>
						}
					>
						<div className={styles.checkboxGroups}>
							{pressTopics.map((topic) => {
								const checked = isChecked('topics', topic.slug);
								const count = getCountBySlug('topics', topic.slug);
								return (
									<CheckboxWithLabel
										checked={checked}
										text={topic.title}
										name={'topics'}
										value={topic.slug}
										key={`topic-${topic.slug}-${checked}`}
										id={`topic-${topic.slug}`}
										onChange={handleCheckboxChange}
										count={count}
										disabled={!count}
									/>
								);
							})}
						</div>
					</CollapsibleDrawer>

					{/* Department */}
					<CollapsibleDrawer
						open={false}
						heading={
							<CheckboxGroupHeading
								name="Met department"
								count={params?.departments?.length}
							/>
						}
					>
						{['Curatorial', 'Conservation', 'Other'].map((departmentGroup) => {
							return (
								<div
									key={`dept-group-${departmentGroup}`}
									className={styles.checkboxGroups}
								>
									<h5>{departmentGroup}</h5>
									{allDepartments[departmentGroup.toLowerCase()].map(
										(department) => {
											const checked = isChecked('departments', department.slug);
											const count = getCountBySlug(
												'departments',
												department.slug,
											);
											return (
												<CheckboxWithLabel
													checked={checked}
													text={department.title}
													name={'departments'}
													value={department.slug}
													key={`department-${department.slug}-${checked}`}
													id={`department-${department.slug}`}
													onChange={handleCheckboxChange}
													count={count}
													disabled={!count}
												/>
											);
										},
									)}
								</div>
							);
						})}
					</CollapsibleDrawer>
				</div>
			</SearchLayout>
		</PageSection>
	);
};

export default PressSearch;
